/**
 * @file src/views/VID/vidStorageKey.ts
 * @exports VID_SESSION_STORAGE_KEY, isVidPathExemptFromPartitionGuard, persistVidPartitionKeyToTabSession, resolveAndPersistVidPartitionKey
 * @summary Derives the Jotai/localStorage/IndexedDB partition key from router query plus sessionStorage (shared by Manager and React hooks).
 * @remarks Single source of truth for session storage key resolution; keep Manager.getVidSessionId and session hooks in sync.
 *          Under `/vid/*`, there is no fallback: missing invite parameters and session storage yields `null` (fail loudly).
 *          Outside `/vid/*`, a per-tab id is created and persisted so Cognition can use the same atoms without collisions across tabs.
 *          URL search state is supplied explicitly by callers; tab session storage bridges redirects before hooks rerender.
 */

import { paths } from "src/const";

export const VID_SESSION_STORAGE_KEY = "vidStorageKey";

/** Resolved URL prefix for invite pages (e.g. `/vid/i/<token>`). Derived from `paths.vidInvite` so route changes stay in one place. */
const vidInviteResolvedPathPrefix = `${paths.vidInvite.replace(/\/\[[^\]]+]$/, "")}/`;

/**
 * @summary vID routes that must render before a partition key exists (landing, encoded invite URL, remote-capture QR bootstrap, service outage).
 * @remarks Next.js exposes the **resolved** invite path (`/vid/i/<token>`), not the file pattern in `paths.vidInvite`, so the guard cannot rely only on the bracket form.
 *          `/vid/service-unavailable` is exempt so CRA outages can reach the page without a vID session key.
 *          CRA-only UI that reaches vID partition hooks (via `useFetchData` → `useSessionValue`) must not mount on these
 *          routes: the partition does not exist until `processInviteString` runs.
 */
export const isVidPathExemptFromPartitionGuard = (pathname: string): boolean => {
	if (
		pathname === paths.vidLanding ||
		pathname === paths.vidInvite ||
		pathname === paths.vidRemoteCaptureInit ||
		pathname === paths.vidServiceUnavailable
	) {
		return true;
	}
	return pathname.startsWith(vidInviteResolvedPathPrefix) && pathname.length > vidInviteResolvedPathPrefix.length;
};

const queryStringParam = (value: unknown): string | undefined => {
	if (typeof value === "string" && value) {
		return value;
	}
	if (Array.isArray(value) && typeof value[0] === "string" && value[0]) {
		return value[0];
	}
	return undefined;
};

const readVidStorageKeyFromSessionStorage = (): string | null => {
	if (typeof window === "undefined") {
		return null;
	}
	try {
		return window.sessionStorage.getItem(VID_SESSION_STORAGE_KEY);
	} catch {
		return null;
	}
};

const writeVidStorageKeyToSessionStorage = (key: string) => {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.sessionStorage.setItem(VID_SESSION_STORAGE_KEY, key);
	} catch {
		// ignore
	}
};

/**
 * @summary Persists the partition id to tab `sessionStorage` (`VID_SESSION_STORAGE_KEY`).
 * @remarks Call before navigation so session writes use the same partition while route search state updates.
 */
export const persistVidPartitionKeyToTabSession = (partitionKey: string): void => {
	writeVidStorageKeyToSessionStorage(partitionKey);
};

/** One key per browser tab for non–vID-web surfaces (Cognition, etc.); avoids sharing a global default partition. */
const createAndPersistBrowserTabPartitionKey = (): string => {
	const id =
		typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.randomUUID === "function"
			? globalThis.crypto.randomUUID()
			: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
	const key = `browserTab_${id}`;
	writeVidStorageKeyToSessionStorage(key);
	return key;
};

/**
 * @summary Resolves the Jotai/localStorage/IndexedDB partition id from `routerQuery`, `pathname`, and session storage.
 * @remarks When `vidSessionId` or `voucherId` appear in `routerQuery`, copies them into `sessionStorage` (`VID_SESSION_STORAGE_KEY`) so later navigations stay partitioned.
 * @returns `null` on `/vid/*` when query and session storage provide no key. Outside `/vid/*`, allocates a per-tab key in the browser when needed.
 */
export const resolveAndPersistVidPartitionKey = (
	routerQuery: Record<string, unknown>,
	pathname: string,
): string | null => {
	const query = routerQuery;

	const fromQueryVidSessionId = queryStringParam(query.vidSessionId);
	if (fromQueryVidSessionId) {
		writeVidStorageKeyToSessionStorage(fromQueryVidSessionId);
		return fromQueryVidSessionId;
	}

	// Standard (non-api-key) vID web entrypoint typically includes voucherId.
	// Derive a stable per-journey key and persist it per-tab so subsequent steps
	// keep using the same partition even if voucherId is no longer in the URL.
	const fromQueryVoucherId = queryStringParam(query.voucherId);
	if (fromQueryVoucherId) {
		const derived = `session_${fromQueryVoucherId}`;
		writeVidStorageKeyToSessionStorage(derived);
		return derived;
	}

	const fromSessionStorage = readVidStorageKeyFromSessionStorage();
	if (fromSessionStorage) {
		return fromSessionStorage;
	}

	if (pathname.startsWith("/vid/")) {
		return null;
	}

	if (typeof window === "undefined") {
		return null;
	}

	return createAndPersistBrowserTabPartitionKey();
};
